import { Clock, DollarSign, Receipt } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMinutesToReadable, formatCurrency } from "@/lib/timer-calculations";
import { cn } from "@/lib/utils";

interface DailySummaryCardsProps {
  totalMinutes: number;
  billableMinutes: number;
  nonBillableMinutes: number;
  billableValue: number;
  isLoading?: boolean;
}

export function DailySummaryCards({
  totalMinutes,
  billableMinutes,
  nonBillableMinutes,
  billableValue,
  isLoading = false,
}: DailySummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    );
  }

  const billablePercentage = totalMinutes > 0 
    ? Math.round((billableMinutes / totalMinutes) * 100) 
    : 0;

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {/* Total Hours */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Clock className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total do dia</p>
              <p className="text-lg font-bold">
                {formatMinutesToReadable(totalMinutes)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billable vs Non-Billable */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-500/10 p-2">
              <Receipt className="h-4 w-4 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Faturável</p>
              <div className="flex items-baseline gap-2">
                <p className="text-lg font-bold text-green-600">
                  {formatMinutesToReadable(billableMinutes)}
                </p>
                <span className="text-xs text-muted-foreground">
                  ({billablePercentage}%)
                </span>
              </div>
              {nonBillableMinutes > 0 && (
                <p className="text-xs text-muted-foreground">
                  Não faturável: {formatMinutesToReadable(nonBillableMinutes)}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billable Value */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-yellow-500/10 p-2">
              <DollarSign className="h-4 w-4 text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Valor do dia</p>
              <p className={cn(
                "text-lg font-bold",
                billableValue > 0 ? "text-yellow-600" : "text-muted-foreground"
              )}>
                {billableValue > 0 ? formatCurrency(billableValue) : "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
